import { CommonModule } from '@angular/common';
import { Component, HostBinding, Input, OnDestroy, signal } from '@angular/core';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MobileTooltipTriggerDirective } from '@shared/directives';
import { RouterModule } from '@angular/router';
import { AuthService } from '@core/services/auth.service';
import {OwnSubscriptionShort, UserService} from '@core/services/user.service';
import { TranslateModule } from '@ngx-translate/core';
import { DashboardService } from '@v3/dashboard/dashboard.service';
import moment from 'moment';
import { userType } from '@core/models';

@Component({
    selector: 'ms-subscription-status',
    imports: [
        CommonModule,
        RouterModule,
        MatTooltipModule, MobileTooltipTriggerDirective,
        TranslateModule,
        MatProgressSpinnerModule,
    ],
    templateUrl: './subscription-status.component.html',
    styleUrls: ['./subscription-status.component.scss']
})
export class SubscriptionStatusComponent implements OnDestroy {
  @HostBinding('class')
  @Input()
    color: 'purple' | 'yellow' = 'purple';

  // Signal для принудительного обновления
  private readonly updateTrigger = signal(0);

  // Timer для обновления времени
  private timer: number | null = null;

  get isStudent() {
    return this._authService.accountType === 'student';
  }

  get accountType(): userType {
    return this._authService.accountType;
  }

  get ownSubscriptions() {
    return this.userService.ownSubscriptions.filter(subscription => subscription.status === 'active');
  }

  get hasOldSubscriptions(): boolean {
    return this.userService.isOldSubscriptionsExists;
  }

  @HostBinding('class.shrink')
  get isShrink() {
    return this._dashboardService.isShrink;
  }

  constructor(
    public readonly userService: UserService,
    private readonly _authService: AuthService,
    private readonly _dashboardService: DashboardService,
  ) {
    // Запускаем обновление времени каждую минуту
    this.startTimeUpdates();
  }

  /**
   * Вычисляет время до истечения подписки в часах и минутах
   * @param subscription - объект подписки
   * @returns объект с часами и минутами или null если больше 0 дней
   */
  getTimeUntilExpiration(subscription: OwnSubscriptionShort): { hours: number; minutes: number } | null {
    if (subscription.availableDays > 0) {
      return null;
    }

    // Читаем signal для принудительного обновления
    this.updateTrigger();

    // Получаем текущее время UTC и добавляем 3 часа для МСК
    const now = moment().utc().add(3, 'hours');

    // Следующая полночь по МСК
    const nextMidnight = now.clone().add(1, 'day').startOf('day');

    // Вычисляем разность
    const duration = moment.duration(nextMidnight.diff(now));

    const hours = Math.floor(duration.asHours());
    const minutes = duration.minutes();

    return { hours, minutes };
  }

  private startTimeUpdates(): void {
    // Обновляем время каждую минуту
    this.timer = window.setInterval(() => {
      // Обновляем signal для принудительного обновления
      this.updateTrigger.set(this.updateTrigger() + 1);
    }, 60000);
  }

  ngOnDestroy(): void {
    if (this.timer) {
      clearInterval(this.timer);
    }
  }
}
